// POWER UPS unlocks in Run Setup (2026-11-02): the starting skill (Opening Gambit), starting trait (Trait Primer) and the
// pre-run banishes (Exile Seal). Pure (no DOM): builds the Run Setup rows from the profile and turns the stored picks into
// SimOptions fields. Picks are remembered per device like the other Run Setup options ('arena.pu.*', so they travel with
// a save export's prefs); a pick that is no longer offered (refunded unlock, Power Ups Off) silently falls back to None.
import { INNATE, PASSIVES, PassiveId, WEAPONS, WeaponId, WorkshopRanks } from '../core/content';
import { catalogue, Codex, isDiscovered } from './codex';
import type { ExtraRow } from './prerun';

export const PU_KEYS = { skill: 'pu.start', trait: 'pu.trait', banish: ['pu.ban1', 'pu.ban2', 'pu.ban3'] } as const;
const NONE = { value: '', text: 'None' };

export interface LoadoutCtx {
  ranks: WorkshopRanks;
  codex: Codex;
  /** Run Setup Power Ups row: Off hides the rows and ignores every pick */
  powerUps: boolean;
  /** the character's own starting technique (never offered: it is already in the kit) */
  kitSkill: WeaponId;
  get(k: string): string | null;
}

/** discovered skills a run may start with (no innate, no retired, not the kit skill) */
export function startSkillIds(c: Codex, kitSkill: WeaponId): WeaponId[] {
  return (catalogue('skills') as WeaponId[]).filter((id) => id !== kitSkill && !INNATE.includes(id) && !WEAPONS[id].retired && isDiscovered(c, 'skills', id));
}
export function startTraitIds(c: Codex): PassiveId[] {
  return (catalogue('traits') as PassiveId[]).filter((id) => isDiscovered(c, 'traits', id));
}
const nameOf = (id: string) => (id in WEAPONS ? WEAPONS[id as WeaponId].name : id in PASSIVES ? PASSIVES[id as PassiveId].name : id);
const pick = (v: string | null, ok: readonly string[]) => (v && ok.includes(v) ? v : '');

/** The picks that apply to the next run (validated; '' = none). Power Ups Off or a missing unlock → nothing. */
export function loadout(x: LoadoutCtx): { startSkill?: string; startTrait?: string; banish?: string[] } {
  if (!x.powerUps) return {};
  const out: { startSkill?: string; startTrait?: string; banish?: string[] } = {};
  if ((x.ranks.opener ?? 0) > 0) { const s = pick(x.get(PU_KEYS.skill), startSkillIds(x.codex, x.kitSkill)); if (s) out.startSkill = s; }
  if ((x.ranks.primer ?? 0) > 0) { const t = pick(x.get(PU_KEYS.trait), startTraitIds(x.codex)); if (t) out.startTrait = t; }
  const n = Math.min(3, x.ranks.exile ?? 0);
  if (n > 0) {
    const ok = banishIds(x, out.startSkill, out.startTrait), seen = new Set<string>(), list: string[] = [];
    for (const k of PU_KEYS.banish.slice(0, n)) { const b = pick(x.get(k), ok); if (b && !seen.has(b)) { seen.add(b); list.push(b); } }
    if (list.length) out.banish = list;
  }
  return out;
}

/** what can be banished: discovered skills and traits, except the kit and this run's starting picks */
function banishIds(x: LoadoutCtx, skill?: string, trait?: string): string[] {
  return [...startSkillIds(x.codex, x.kitSkill), ...startTraitIds(x.codex)].filter((id) => id !== skill && id !== trait);
}

/** Run Setup rows for the bought unlocks (none while Power Ups is Off). */
export function loadoutRows(x: LoadoutCtx): ExtraRow[] {
  if (!x.powerUps) return [];
  const rows: ExtraRow[] = [];
  const cur = () => loadout(x);
  if ((x.ranks.opener ?? 0) > 0) rows.push({
    key: PU_KEYS.skill, label: 'Starting skill',
    options: () => [NONE, ...startSkillIds(x.codex, x.kitSkill).map((id) => ({ value: id, text: WEAPONS[id].name }))],
    desc: (v) => v ? `Opening Gambit: the run starts with ${nameOf(v)} at rank 1, on top of your usual kit (it takes a skill slot).`
      : `Opening Gambit: start with any skill you have discovered (${startSkillIds(x.codex, x.kitSkill).length} so far). None = the usual kit.`,
  });
  if ((x.ranks.primer ?? 0) > 0) rows.push({
    key: PU_KEYS.trait, label: 'Starting trait',
    options: () => [NONE, ...startTraitIds(x.codex).map((id) => ({ value: id, text: PASSIVES[id].name }))],
    desc: (v) => v ? `Trait Primer: the run starts with ${nameOf(v)} at rank 1 (it takes a trait slot). ${PASSIVES[v as PassiveId]?.desc ?? ''}`
      : `Trait Primer: start with an extra trait you have discovered (${startTraitIds(x.codex).length} so far).`,
  });
  const n = Math.min(3, x.ranks.exile ?? 0);
  for (let k = 0; k < n; k++) {
    const key = PU_KEYS.banish[k];
    rows.push({
      key, label: n > 1 ? `Banish ${k + 1}` : 'Banish',
      options: () => {
        const c = cur(), taken = new Set(PU_KEYS.banish.filter((o) => o !== key).map((o) => x.get(o) ?? ''));
        return [NONE, ...banishIds(x, c.startSkill, c.startTrait).filter((id) => !taken.has(id)).map((id) => ({ value: id, text: nameOf(id) }))];
      },
      desc: (v) => v ? `Exile Seal: ${nameOf(v)} never appears in level-up drafts or chests this run.`
        : `Exile Seal: remove a discovered skill or trait from the draft pool for the whole run (${n} per run).`,
    });
  }
  return rows;
}
